'use client';

import { createClient } from '@/lib/supabase/client';
import type { SupabaseClient, User } from '@supabase/supabase-js';
import { appCache, cacheKey, TTL } from '@/lib/cache/memoryCache';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SignalStatus =
  | 'draft' | 'waiting' | 'approved' | 'entered' |'invalidated' | 'closed-win' | 'closed-loss' | 'breakeven';

export interface Signal {
  id: string;
  symbol: string;
  direction: 'buy' | 'sell';
  obTF: string;
  entryLow: number;
  entryHigh: number;
  sl: number;
  tp1: number;
  tp2: number;
  rr: number;
  confidence: number;
  status: SignalStatus;
  mtfConflict: boolean;
  volumeNote: string;
  reason: string;
  createdAt: string;
}

export interface JournalEntry {
  id: string;
  date: string;
  symbol: string;
  direction: 'buy' | 'sell';
  obType: string;
  obTF: string;
  entryPrice: number;
  exitPrice: number;
  stopLoss: number;
  lotSize: number;
  pnlUsd: number;
  pnlR: number;
  riskUsd: number;
  outcome: 'win' | 'loss' | 'breakeven' | 'not_taken';
  session: string;
  confidence: number;
  mistakeCategory: string | null;
  lesson: string;
  signalId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aiReasoning: Record<string, any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aiObValidation: Record<string, any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aiTpslSuggestions: Record<string, any> | null;
}

export interface OrderBlock {
  id: string;
  symbol: string;
  direction: 'buy' | 'sell';
  tf: string;
  obHigh: number;
  obLow: number;
  freshness: 'fresh' | 'partial' | 'mitigated';
  volumeStatus: string;
  mitigation: string;
  signalId: string | null;
  touchCount: number;
  age: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isMissingUserIdColumn(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === '42703' && (error.message?.includes('user_id') ?? false);
}

function isSchemaError(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return patterns.some((p) => p.test(error.message!));
  }
  return false;
}

/**
 * Scope queries to the authenticated user when the user_id column exists.
 * Generic `T` (without a self-referential constraint) preserves the original
 * query builder type — keeping result rows typed while avoiding the PostgREST
 * builder recursion that triggers TS2589.
 */
function scopeToUser<T>(query: T, user: User, useUserId: boolean): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (useUserId ? (query as any).eq('user_id', user.id) : query) as T;
}

type UserScope = { user: User; useUserId: boolean };

let userIdColumnCache: boolean | null = null;
// ─── User / session cache ─────────────────────────────────────────────────────
// Caches the resolved User object so repeated write operations within the same
// session do not each pay the cost of a getUser() round-trip.
let cachedUser: User | null = null;

/** Call this whenever the auth state changes to flush the user cache. */
export function invalidateUserCache(): void {
  cachedUser = null;
  userIdColumnCache = null;
  // Also clear all app-level caches on auth change
  appCache.clear();
}

async function getUserScope(supabase: SupabaseClient): Promise<UserScope | null> {
  // Use cached user when available — avoids a network round-trip per write
  if (!cachedUser) {
    const { data: { user: fetchedUser } } = await supabase.auth.getUser();
    if (!fetchedUser) return null;
    cachedUser = fetchedUser;
  }
  const user = cachedUser;

  // journal_entries.user_id column is confirmed to exist in the schema.
  // Always use user_id scoping — skip the probe query that could fail under RLS.
  userIdColumnCache = true;

  return { user, useUserId: userIdColumnCache };
}

/** Build an insert/update payload that includes user_id only when the column exists. */
export async function buildUserPayload(
  supabase: SupabaseClient,
  payload: Record<string, unknown>
): Promise<Record<string, unknown> | null> {
  const scope = await getUserScope(supabase);
  if (!scope) return null;
  return withUserId(payload, scope.user, scope.useUserId);
}

function withUserId(payload: Record<string, unknown>, user: User, useUserId: boolean) {
  return useUserId ? { ...payload, user_id: user.id } : payload;
}

function rowToSignal(row: Record<string, unknown>): Signal {
  return {
    id: row.id as string,
    symbol: row.symbol as string,
    direction: row.direction as 'buy' | 'sell',
    obTF: row.ob_tf as string,
    entryLow: Number(row.entry_low),
    entryHigh: Number(row.entry_high),
    sl: Number(row.sl),
    tp1: Number(row.tp1),
    tp2: Number(row.tp2),
    rr: Number(row.rr),
    confidence: row.confidence as number,
    status: row.signal_status as SignalStatus,
    mtfConflict: row.mtf_conflict as boolean,
    volumeNote: row.volume_note as string,
    reason: row.reason as string,
    createdAt: new Date(row.created_at as string).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    }),
  };
}

function rowToJournal(row: Record<string, unknown>): JournalEntry {
  return {
    id: row.id as string,
    date: normalizeTradeDate(row.trade_date),
    symbol: row.symbol as string,
    direction: row.direction as 'buy' | 'sell',
    obType: row.ob_type as string,
    obTF: row.ob_tf as string,
    entryPrice: Number(row.entry_price),
    exitPrice: Number(row.exit_price),
    stopLoss: Number(row.stop_loss ?? 0),
    lotSize: Number(row.lot_size),
    pnlUsd: Number(row.pnl_usd),
    pnlR: Number(row.pnl_r),
    riskUsd: Number(row.risk_usd ?? 0),
    outcome: row.outcome as 'win' | 'loss' | 'breakeven' | 'not_taken',
    session: row.session as string,
    confidence: row.confidence as number,
    mistakeCategory: (row.mistake_category as string) ?? null,
    lesson: row.lesson as string,
    signalId: (row.signal_id as string) ?? '',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aiReasoning: (row.ai_reasoning as Record<string, any>) ?? null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aiObValidation: (row.ai_ob_validation as Record<string, any>) ?? null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aiTpslSuggestions: (row.ai_tpsl_suggestions as Record<string, any>) ?? null,
  };
}

/**
 * Normalises any trade_date string to YYYY-MM-DD.
 * Handles:
 *   - "12 Nov 2025"  (en-GB locale format stored by older form)
 *   - "2025-11-12"   (ISO date — already correct)
 *   - "2025-11-12T00:00:00+00:00"  (ISO timestamp)
 *   - null / undefined → empty string (skipped by calendar)
 */
function normalizeTradeDate(raw: unknown): string {
  if (!raw || typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';

  // Already YYYY-MM-DD or ISO timestamp — just take first 10 chars
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  // "DD Mon YYYY" format e.g. "12 Nov 2025"
  const monthMap: Record<string, string> = {
    jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
    jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
  };
  const match = s.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (match) {
    const day = match[1].padStart(2, '0');
    const mon = monthMap[match[2].toLowerCase()];
    const year = match[3];
    if (mon) return `${year}-${mon}-${day}`;
  }

  // Fallback: try native Date parse and extract YYYY-MM-DD
  try {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
  } catch {
    // ignore
  }

  return s.slice(0, 10);
}

function rowToOB(row: Record<string, unknown>): OrderBlock {
  return {
    id: row.id as string,
    symbol: row.symbol as string,
    direction: row.direction as 'buy' | 'sell',
    tf: row.tf as string,
    obHigh: Number(row.ob_high),
    obLow: Number(row.ob_low),
    freshness: row.freshness as 'fresh' | 'partial' | 'mitigated',
    volumeStatus: row.volume_status as string,
    mitigation: row.mitigation as string,
    signalId: (row.signal_id as string) ?? null,
    touchCount: row.touch_count as number,
    age: row.age_label as string,
  };
}

// ─── Signal Service ───────────────────────────────────────────────────────────

export const signalService = {
  async getAll(): Promise<Signal[]> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return [];
    try {
      const { data, error } = await scopeToUser(
        supabase.from('signals').select('*'),
        scope.user,
        scope.useUserId
      ).order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('signals fetch error:', error.message);
        return [];
      }
      return (data || []).map(rowToSignal);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('signalService.getAll error:', message);
      throw e;
    }
  },

  async create(payload: Omit<Signal, 'createdAt'>): Promise<Signal | null> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const { data, error } = await supabase
        .from('signals')
        .insert(withUserId({
          id: payload.id,
          symbol: payload.symbol,
          direction: payload.direction,
          ob_tf: payload.obTF,
          entry_low: payload.entryLow,
          entry_high: payload.entryHigh,
          sl: payload.sl,
          tp1: payload.tp1,
          tp2: payload.tp2,
          rr: payload.rr,
          confidence: payload.confidence,
          signal_status: payload.status,
          mtf_conflict: payload.mtfConflict,
          volume_note: payload.volumeNote,
          reason: payload.reason,
        }, scope.user, scope.useUserId))
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('signal create error:', error.message);
        return null;
      }
      return data ? rowToSignal(data) : null;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('signalService.create error:', message);
      throw e;
    }
  },

  async updateStatus(id: string, status: SignalStatus): Promise<void> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return;
    try {
      let query = supabase.from('signals').update({ signal_status: status }).eq('id', id);
      if (scope.useUserId) query = query.eq('user_id', scope.user.id);
      const { error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('signal update error:', error.message);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('signalService.updateStatus error:', message);
      throw e;
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return;
    try {
      let query = supabase.from('signals').delete().eq('id', id);
      if (scope.useUserId) query = query.eq('user_id', scope.user.id);
      const { error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('signal delete error:', error.message);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('signalService.delete error:', message);
      throw e;
    }
  },

  async getStatusCounts(): Promise<Record<SignalStatus, number>> {
    const supabase = createClient();
    const empty: Record<SignalStatus, number> = {
      draft: 0, waiting: 0, approved: 0, entered: 0,
      invalidated: 0, 'closed-win': 0, 'closed-loss': 0, breakeven: 0,
    };
    const scope = await getUserScope(supabase);
    if (!scope) return empty;
    try {
      const { data, error } = await scopeToUser(
        supabase.from('signals').select('signal_status'),
        scope.user,
        scope.useUserId
      );
      if (error || !data) return empty;
      const counts = { ...empty };
      data.forEach((r) => {
        const s = r.signal_status as SignalStatus;
        if (s in counts) counts[s]++;
      });
      return counts;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('signalService.getStatusCounts error:', message);
      return empty;
    }
  },
};

// ─── Journal Service ──────────────────────────────────────────────────────────

export const journalService = {
  async getAll(): Promise<JournalEntry[]> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return [];

    // Serve from cache when available
    const key = cacheKey.journalEntries(scope.user.id);
    const cached = appCache.get<JournalEntry[]>(key);
    if (cached !== undefined) return cached;

    try {
      const { data, error } = await scopeToUser(
        supabase.from('journal_entries').select('*'),
        scope.user,
        scope.useUserId
      ).order('created_at', { ascending: false });
      if (error) {
        console.error('journal fetch error:', error.code, error.message);
        if (isSchemaError(error)) throw error;
        return [];
      }
      const entries = (data || []).map(rowToJournal);
      appCache.set(key, entries, TTL.JOURNAL_ENTRIES);
      return entries;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.error('journalService.getAll error:', message);
      throw e;
    }
  },

  async create(entry: Omit<JournalEntry, 'aiReasoning' | 'aiObValidation' | 'aiTpslSuggestions'> & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aiReasoning?: Record<string, any> | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aiObValidation?: Record<string, any> | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aiTpslSuggestions?: Record<string, any> | null;
  }): Promise<JournalEntry | null> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const id = entry.id || `JRN-${Date.now()}`;
      const { data, error } = await supabase
        .from('journal_entries')
        .insert(withUserId({
          id,
          trade_date: entry.date,
          symbol: entry.symbol,
          direction: entry.direction,
          ob_type: entry.obType,
          ob_tf: entry.obTF,
          entry_price: entry.entryPrice,
          exit_price: entry.exitPrice,
          stop_loss: entry.stopLoss ?? 0,
          lot_size: entry.lotSize,
          pnl_usd: entry.pnlUsd,
          pnl_r: entry.pnlR,
          risk_usd: entry.riskUsd ?? 0,
          outcome: entry.outcome,
          session: entry.session,
          confidence: entry.confidence,
          mistake_category: entry.mistakeCategory ?? null,
          lesson: entry.lesson,
          signal_id: entry.signalId || null,
          ai_reasoning: entry.aiReasoning ?? null,
          ai_ob_validation: entry.aiObValidation ?? null,
          ai_tpsl_suggestions: entry.aiTpslSuggestions ?? null,
        }, scope.user, scope.useUserId))
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('journal create error:', error.message);
        return null;
      }
      const result = data ? rowToJournal(data) : null;
      appCache.invalidate(cacheKey.journalEntries(scope.user.id));
      appCache.invalidate(cacheKey.journalStats(scope.user.id));
      return result;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('journalService.create error:', message);
      throw e;
    }
  },

  async update(id: string, entry: Partial<Omit<JournalEntry, 'id' | 'aiReasoning' | 'aiObValidation' | 'aiTpslSuggestions'>>): Promise<JournalEntry | null> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const payload: Record<string, unknown> = {};
      if (entry.date !== undefined) payload.trade_date = entry.date;
      if (entry.symbol !== undefined) payload.symbol = entry.symbol;
      if (entry.direction !== undefined) payload.direction = entry.direction;
      if (entry.obType !== undefined) payload.ob_type = entry.obType;
      if (entry.obTF !== undefined) payload.ob_tf = entry.obTF;
      if (entry.entryPrice !== undefined) payload.entry_price = entry.entryPrice;
      if (entry.exitPrice !== undefined) payload.exit_price = entry.exitPrice;
      if (entry.stopLoss !== undefined) payload.stop_loss = entry.stopLoss;
      if (entry.lotSize !== undefined) payload.lot_size = entry.lotSize;
      if (entry.pnlUsd !== undefined) payload.pnl_usd = entry.pnlUsd;
      if (entry.pnlR !== undefined) payload.pnl_r = entry.pnlR;
      if (entry.riskUsd !== undefined) payload.risk_usd = entry.riskUsd;
      if (entry.outcome !== undefined) payload.outcome = entry.outcome;
      if (entry.session !== undefined) payload.session = entry.session;
      if (entry.confidence !== undefined) payload.confidence = entry.confidence;
      if (entry.mistakeCategory !== undefined) payload.mistake_category = entry.mistakeCategory;
      if (entry.lesson !== undefined) payload.lesson = entry.lesson;
      payload.updated_at = new Date().toISOString();

      let query = supabase.from('journal_entries').update(payload).eq('id', id);
      if (scope.useUserId) query = query.eq('user_id', scope.user.id);
      const { data, error } = await query.select().single();
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('journal update error:', error.message);
        return null;
      }
      const result = data ? rowToJournal(data) : null;
      appCache.invalidate(cacheKey.journalEntries(scope.user.id));
      appCache.invalidate(cacheKey.journalStats(scope.user.id));
      return result;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('journalService.update error:', message);
      throw e;
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return;
    try {
      let query = supabase.from('journal_entries').delete().eq('id', id);
      if (scope.useUserId) query = query.eq('user_id', scope.user.id);
      const { error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('journal delete error:', error.message);
      }
      appCache.invalidate(cacheKey.journalEntries(scope.user.id));
      appCache.invalidate(cacheKey.journalStats(scope.user.id));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('journalService.delete error:', message);
      throw e;
    }
  },

  async getStats(): Promise<{ totalPnl: number; winCount: number; lossCount: number; beCount: number; avgRR: number; worstLoss: number }> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return { totalPnl: 0, winCount: 0, lossCount: 0, beCount: 0, avgRR: 0, worstLoss: 0 };

    // Serve from cache when available
    const key = cacheKey.journalStats(scope.user.id);
    const cached = appCache.get<{ totalPnl: number; winCount: number; lossCount: number; beCount: number; avgRR: number; worstLoss: number }>(key);
    if (cached !== undefined) return cached;

    try {
      const { data, error } = await scopeToUser(
        supabase.from('journal_entries').select('pnl_usd, pnl_r, outcome'),
        scope.user,
        scope.useUserId
      );
      if (error || !data) return { totalPnl: 0, winCount: 0, lossCount: 0, beCount: 0, avgRR: 0, worstLoss: 0 };
      const totalPnl = data.reduce((s, r) => s + Number(r.pnl_usd), 0);
      const winCount = data.filter((r) => r.outcome === 'win').length;
      const lossCount = data.filter((r) => r.outcome === 'loss').length;
      const beCount = data.filter((r) => r.outcome === 'breakeven').length;
      const avgRR = data.length > 0 ? data.reduce((s, r) => s + Number(r.pnl_r), 0) / data.length : 0;
      const losses = data.filter((r) => r.outcome === 'loss').map((r) => Number(r.pnl_usd));
      const worstLoss = losses.length > 0 ? Math.min(...losses) : 0;
      const result = { totalPnl, winCount, lossCount, beCount, avgRR, worstLoss };
      appCache.set(key, result, TTL.JOURNAL_STATS);
      return result;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('journalService.getStats error:', message);
      return { totalPnl: 0, winCount: 0, lossCount: 0, beCount: 0, avgRR: 0, worstLoss: 0 };
    }
  },
};

// ─── Order Block Service ──────────────────────────────────────────────────────

export const obService = {
  async getAll(): Promise<OrderBlock[]> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return [];
    try {
      const { data, error } = await scopeToUser(
        supabase.from('order_blocks').select('*'),
        scope.user,
        scope.useUserId
      ).order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('ob fetch error:', error.message);
        return [];
      }
      return (data || []).map(rowToOB);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('obService.getAll error:', message);
      throw e;
    }
  },

  async getBySymbol(symbol: string): Promise<OrderBlock[]> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return [];
    try {
      const { data, error } = await scopeToUser(
        supabase.from('order_blocks').select('*').eq('symbol', symbol),
        scope.user,
        scope.useUserId
      ).order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(rowToOB);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('obService.getBySymbol error:', message);
      return [];
    }
  },

  async create(ob: Omit<OrderBlock, 'age'>): Promise<OrderBlock | null> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const { data, error } = await supabase
        .from('order_blocks')
        .insert(withUserId({
          id: ob.id,
          symbol: ob.symbol,
          direction: ob.direction,
          tf: ob.tf,
          ob_high: ob.obHigh,
          ob_low: ob.obLow,
          freshness: ob.freshness,
          volume_status: ob.volumeStatus,
          mitigation: ob.mitigation,
          signal_id: ob.signalId ?? null,
          touch_count: ob.touchCount,
          age_label: '',
        }, scope.user, scope.useUserId))
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('ob create error:', error.message);
        return null;
      }
      return data ? rowToOB(data) : null;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('obService.create error:', message);
      throw e;
    }
  },

  async updateFreshness(id: string, freshness: 'fresh\' | \'partial\' | \'mitigated', touchCount: number): Promise<void> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return;
    try {
      let query = supabase.from('order_blocks').update({ freshness, touch_count: touchCount }).eq('id', id);
      if (scope.useUserId) query = query.eq('user_id', scope.user.id);
      const { error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('ob update error:', error.message);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('obService.updateFreshness error:', message);
      throw e;
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return;
    try {
      let query = supabase.from('order_blocks').delete().eq('id', id);
      if (scope.useUserId) query = query.eq('user_id', scope.user.id);
      const { error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('ob delete error:', error.message);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('obService.delete error:', message);
      throw e;
    }
  },
};

// ─── Analytics Service ────────────────────────────────────────────────────────

export const analyticsService = {
  async getKPIs() {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const { data, error } = await scopeToUser(
        supabase.from('journal_entries').select('outcome, pnl_r, confidence, session, ob_tf, symbol, mistake_category'),
        scope.user,
        scope.useUserId
      );
      if (error || !data || data.length === 0) return null;

      const total = data.length;
      const wins = data.filter((r) => r.outcome === 'win').length;
      const losses = data.filter((r) => r.outcome === 'loss').length;
      const bes = data.filter((r) => r.outcome === 'breakeven').length;
      const winRate = total > 0 ? ((wins / total) * 100).toFixed(1) : '0.0';
      const avgRR = total > 0
        ? (data.reduce((s, r) => s + Number(r.pnl_r), 0) / total).toFixed(2)
        : '0.00';

      const sessionMap: Record<string, { wins: number; total: number }> = {};
      data.forEach((r) => {
        if (!sessionMap[r.session]) sessionMap[r.session] = { wins: 0, total: 0 };
        sessionMap[r.session].total++;
        if (r.outcome === 'win') sessionMap[r.session].wins++;
      });
      let bestSession = 'N/A';
      let bestSessionWR = 0;
      let bestSessionCount = 0;
      Object.entries(sessionMap).forEach(([s, v]) => {
        const wr = v.total > 0 ? v.wins / v.total : 0;
        if (wr > bestSessionWR) { bestSession = s; bestSessionWR = wr; bestSessionCount = v.total; }
      });

      const highConfidence = data.filter((r) => r.confidence >= 70).length;
      const volConfRate = total > 0 ? ((highConfidence / total) * 100).toFixed(1) : '0.0';
      const obHitRate = total > 0 ? ((wins / total) * 100).toFixed(1) : '0.0';

      return {
        winRate: `${winRate}%`,
        obHitRate: `${obHitRate}%`,
        avgRR: `1:${avgRR}`,
        volConfRate: `${volConfRate}%`,
        bestSession,
        bestSessionWR: Math.round(bestSessionWR * 100),
        bestSessionCount,
        totalTrades: total,
        wins,
        losses,
        bes,
      };
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('analyticsService.getKPIs error:', message);
      return null;
    }
  },

  async getEquityCurve() {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const { data, error } = await scopeToUser(
        supabase.from('journal_entries').select('pnl_usd, created_at'),
        scope.user,
        scope.useUserId
      ).order('created_at', { ascending: true });
      if (error || !data || data.length === 0) return null;

      let equity = 10000;
      return data.map((r) => {
        equity += Number(r.pnl_usd);
        const d = new Date(r.created_at);
        const label = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        return { date: label, equity: Math.round(equity) };
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('analyticsService.getEquityCurve error:', message);
      return null;
    }
  },

  async getWinRateBySymbol() {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const { data, error } = await scopeToUser(
        supabase.from('journal_entries').select('symbol, outcome'),
        scope.user,
        scope.useUserId
      );
      if (error || !data || data.length === 0) return null;

      const map: Record<string, { wins: number; total: number }> = {};
      data.forEach((r) => {
        if (!map[r.symbol]) map[r.symbol] = { wins: 0, total: 0 };
        map[r.symbol].total++;
        if (r.outcome === 'win') map[r.symbol].wins++;
      });

      const colors = ['var(--positive)', 'var(--primary)', 'var(--primary)', 'var(--warning)', 'var(--warning)', 'var(--negative)'];
      return Object.entries(map)
        .map(([symbol, v], i) => ({
          symbol,
          winRate: Math.round((v.wins / v.total) * 100),
          trades: v.total,
          color: colors[Math.min(i, colors.length - 1)],
        }))
        .sort((a, b) => b.winRate - a.winRate);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('analyticsService.getWinRateBySymbol error:', message);
      return null;
    }
  },

  async getOBTypeBreakdown() {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const { data, error } = await scopeToUser(
        supabase.from('journal_entries').select('ob_type, outcome'),
        scope.user,
        scope.useUserId
      );
      if (error || !data || data.length === 0) return null;

      const map: Record<string, { wins: number; total: number }> = {};
      data.forEach((r) => {
        if (!map[r.ob_type]) map[r.ob_type] = { wins: 0, total: 0 };
        map[r.ob_type].total++;
        if (r.outcome === 'win') map[r.ob_type].wins++;
      });

      return Object.entries(map).map(([type, v]) => ({
        type,
        winRate: Math.round((v.wins / v.total) * 100),
        count: v.total,
      }));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('analyticsService.getOBTypeBreakdown error:', message);
      return null;
    }
  },

  async getConfidenceBuckets() {
    const supabase = createClient();
    const scope = await getUserScope(supabase);
    if (!scope) return null;
    try {
      const { data, error } = await scopeToUser(
        supabase.from('journal_entries').select('confidence, outcome'),
        scope.user,
        scope.useUserId
      );
      if (error || !data || data.length === 0) return null;

      const buckets: Record<string, { wins: number; total: number }> = {
        '50–59': { wins: 0, total: 0 },
        '60–69': { wins: 0, total: 0 },
        '70–79': { wins: 0, total: 0 },
        '80–89': { wins: 0, total: 0 },
        '90+': { wins: 0, total: 0 },
      };
      data.forEach((r) => {
        const c = r.confidence;
        const key = c >= 90 ? '90+' : c >= 80 ? '80–89' : c >= 70 ? '70–79' : c >= 60 ? '60–69' : '50–59';
        buckets[key].total++;
        if (r.outcome === 'win') buckets[key].wins++;
      });

      return Object.entries(buckets).map(([bucket, v]) => ({
        bucket,
        winRate: v.total > 0 ? Math.round((v.wins / v.total) * 100) : 0,
        trades: v.total,
      }));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      console.log('analyticsService.getConfidenceBuckets error:', message);
      return null;
    }
  },
};
